import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Attendance service's public contract.
 *
 * A record references a person, and optionally a group, an event or a
 * course, by id alone. Names are resolved through PeopleReadPort,
 * GroupsReadPort, EventsReadPort and AcademicReadPort at read time, the same
 * rule every dependent module follows.
 */

export const ATTENDANCE_CONTEXT_TYPES = ['GROUP', 'EVENT', 'COURSE', 'GENERAL'] as const;
export type AttendanceContextType = (typeof ATTENDANCE_CONTEXT_TYPES)[number];

// --- Requests ---------------------------------------------------------------

export const checkInRequestSchema = z
  .object({
    personId: z.uuid(),
    contextType: z.enum(ATTENDANCE_CONTEXT_TYPES),
    /** Required for GROUP, EVENT and COURSE; must be absent for GENERAL. */
    contextId: z.uuid().nullish(),
    campusId: z.uuid().nullish(),
  })
  .refine((value) => (value.contextType === 'GENERAL') === (value.contextId == null), {
    message: 'contextId is required for GROUP, EVENT and COURSE, and must be omitted for GENERAL',
    path: ['contextId'],
  });
export type CheckInRequest = z.infer<typeof checkInRequestSchema>;

export const listAttendanceQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  personId: z.uuid().nullish(),
  contextType: z.enum(ATTENDANCE_CONTEXT_TYPES).nullish(),
  contextId: z.uuid().nullish(),
  /** Only records with no `checkedOutAt` yet. */
  openOnly: queryBoolean().default(false),
});
export type ListAttendanceQuery = z.infer<typeof listAttendanceQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface AttendanceRecordResponse {
  readonly id: string;
  readonly personId: string;
  readonly displayName: string;
  readonly contextType: AttendanceContextType;
  readonly contextId: string | null;
  /** The group or event name, resolved at read time. Null for GENERAL. */
  readonly contextLabel: string | null;
  readonly campusId: string | null;
  readonly checkedInAt: string;
  readonly checkedOutAt: string | null;
}
